import { useState } from "react";
import {
  Box,
  ClickAwayListener,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import { InfoIcon } from "lucide-react";

import { MetricBadge, MetricBadgeProps } from "./MetricBadge";

type BaseCellProps = {
  cellText?: string | number;
  metricBadgeData?: MetricBadgeProps | null;
  testId: string;
};

type NightReportFieldCellProps = BaseCellProps & {
  title?: string;
  toolTip?: string;
};

export const NightReportFieldCell = ({
  title = "",
  cellText = "",
  metricBadgeData,
  testId,
  toolTip,
}: NightReportFieldCellProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  return isDesktop ? (
    <NightReportCellDesktop
      cellText={cellText}
      testId={testId}
      metricBadgeData={metricBadgeData}
    />
  ) : (
    <NightReportCellSmall
      cellText={cellText}
      testId={testId}
      metricBadgeData={metricBadgeData}
      title={title}
      toolTip={toolTip}
    />
  );
};

export const NightReportCellDesktop = ({
  cellText,
  metricBadgeData,
  testId,
}: BaseCellProps) => {
  return (
    <Box sx={{ display: "flex", gap: 1 }} data-testid={testId}>
      {cellText}
      {metricBadgeData && (
        <MetricBadge
          showPercent={metricBadgeData.showPercent}
          delta={metricBadgeData.delta}
          alertMetric={metricBadgeData.alertMetric}
          metric={metricBadgeData.metric}
          testId={metricBadgeData.testId}
        />
      )}
    </Box>
  );
};

export const NightReportCellSmall = ({
  title,
  cellText,
  metricBadgeData,
  toolTip,
  testId,
}: NightReportFieldCellProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ display: "flex", gap: 0.5, width: "100%" }} data-testid={testId}>
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Box>
          <Typography
            variant="bodyMicroRegular"
            sx={{
              color: theme.vars.palette.texts.neutral.subtle,
            }}
          >
            {title}
          </Typography>
        </Box>

        <Box>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Typography
              variant="bodySmallRegular"
              sx={{
                color: theme.vars.palette.texts.neutral.subtle,
              }}
            >
              {cellText}
            </Typography>
            {metricBadgeData && (
              <MetricBadge
                showPercent={metricBadgeData.showPercent}
                delta={metricBadgeData.delta}
                alertMetric={metricBadgeData.alertMetric}
                metric={metricBadgeData.metric}
                testId={metricBadgeData.testId}
              />
            )}
          </Box>
        </Box>
      </Box>

      {toolTip && (
        <Box
          sx={{
            width: "20px",
            flexShrink: 0,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: isMobile ? "flex-start" : "center",
          }}
        >
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Tooltip open={open} title={toolTip}>
              <InfoIcon size={20} onClick={() => setOpen((open) => !open)} />
            </Tooltip>
          </ClickAwayListener>
        </Box>
      )}
    </Box>
  );
};
